import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';

// Shapes mirror GET /api/v1/diagnostics/infra-health (backend diagnostics.py).
interface RedisCheck { method: string; ok: boolean; ping?: boolean; error?: string; ms?: number; }
interface RedisSection {
  use_redis_socketio: boolean;
  host: string;
  port: number;
  password: string;
  checks: RedisCheck[];
}
interface EngineStatus {
  name: string;
  configured: boolean;
  host?: string;
  database?: string;
  pool?: { size: number; checked_out: number; overflow: number };
  pool_error?: string;
  ping_ok?: boolean;
  ping_ms?: number;
  ping_error?: string;
}
interface DbSection {
  app: EngineStatus;
  live: EngineStatus;
  replica: EngineStatus;
  live_falling_back_to_app: boolean;
}
interface AuthSection {
  allowed_jwt_algorithms: string[];
  guard_jwt_algorithm: string;
  note: string;
  tenant_id_set: boolean;
  api_audience_set: boolean;
}
interface LogSection {
  available: boolean;
  note?: string;
  files_scanned?: string[];
  counts?: Record<string, number>;
}
interface DispatchLiveSection {
  worker_uptime_seconds: number;
  push_enabled: boolean | null;
  push_enabled_error?: string;
  pushes: {
    total: number;
    by_reason: Record<string, number>;
    seconds_since_last: number | null;
  };
  bucket_counts: {
    hits: number;
    misses: number;
    hit_rate_pct: number | null;
    scans: number;
    last_scan_ms: number | null;
    p95_scan_ms: number | null;
    entries: number;
    in_flight: number;
    ttl_seconds: number;
  };
}

interface InfraHealth {
  redis: RedisSection;
  database: DbSection;
  auth: AuthSection;
  logs: LogSection;
  dispatch_live: DispatchLiveSection;
}

/** Metric row. Numbers get tabular-nums so columns line up as values change. */
const Metric: React.FC<{ k: string; children: React.ReactNode; hint?: string }> = ({ k, children, hint }) => (
  <div className="flex gap-2 items-baseline">
    <span className="w-44 shrink-0 text-slate-500 dark:text-slate-400">{k}</span>
    <span className="text-[0.8125rem] tabular-nums font-medium">{children}</span>
    {hint && <span className="text-[0.6875rem] text-slate-400">{hint}</span>}
  </div>
);

const fmtAge = (secs: number | null): string =>
  secs === null ? 'never' : secs < 90 ? `${Math.round(secs)}s ago` : `${Math.round(secs / 60)}m ago`;

const Pill: React.FC<{ ok: boolean; label?: string }> = ({ ok, label }) => (
  <span
    className={
      'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[0.6875rem] font-semibold ' +
      (ok
        ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
        : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300')
    }
  >
    <span className={'h-1.5 w-1.5 rounded-full ' + (ok ? 'bg-green-500' : 'bg-red-500')} />
    {label ?? (ok ? 'OK' : 'FAIL')}
  </span>
);

const Card: React.FC<{ title: string; children: React.ReactNode; right?: React.ReactNode }> = ({
  title,
  children,
  right,
}) => (
  <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-sm">
    <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 dark:border-slate-700">
      <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">{title}</h2>
      {right}
    </div>
    <div className="p-4 text-[0.8125rem] text-slate-700 dark:text-slate-300 space-y-2">{children}</div>
  </div>
);

const KV: React.FC<{ k: string; children: React.ReactNode }> = ({ k, children }) => (
  <div className="flex gap-2">
    <span className="w-40 shrink-0 text-slate-500 dark:text-slate-400">{k}</span>
    <span className="text-[0.75rem] break-all">{children}</span>
  </div>
);

const EngineRow: React.FC<{ e: EngineStatus }> = ({ e }) => (
  <div className="border border-slate-100 dark:border-slate-700 rounded p-2.5">
    <div className="flex items-center justify-between mb-1">
      <span className="font-semibold">{e.name}</span>
      {e.configured ? (
        <Pill ok={!!e.ping_ok} label={e.ping_ok ? `ping ${e.ping_ms}ms` : 'ping fail'} />
      ) : (
        <Pill ok={false} label="not configured" />
      )}
    </div>
    {e.configured && (
      <div className="text-[0.75rem] text-slate-500 dark:text-slate-400 space-y-0.5">
        <div>host: <span>{e.host ?? '—'}</span> · db: <span>{e.database ?? '—'}</span></div>
        {e.pool && (
          <div>pool: size {e.pool.size}, checked_out {e.pool.checked_out}, overflow {e.pool.overflow}</div>
        )}
        {e.ping_error && <div className="text-red-600 dark:text-red-400">ping error: {e.ping_error}</div>}
      </div>
    )}
  </div>
);

const SystemDiagnostics: React.FC = () => {
  const [data, setData] = useState<InfraHealth | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ranAt, setRanAt] = useState<string | null>(null);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get('/diagnostics/infra-health');
      setData(r.data as InfraHealth);
      setRanAt(new Date().toLocaleString());
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    run();
  }, [run]);

  return (
    <div className="min-h-screen bg-[#f5f6f8] dark:bg-gray-900 p-6">
      <div className="max-w-4xl mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">System Diagnostics</h1>
            <p className="text-[0.75rem] text-slate-500 dark:text-slate-400">
              Live infra health from the running backend. {ranAt && <>Last run: {ranAt}</>}
            </p>
          </div>
          <button
            type="button"
            onClick={run}
            disabled={loading}
            className="px-4 py-2 text-[0.8125rem] font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-60"
          >
            {loading ? 'Running…' : 'Run checks'}
          </button>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-md px-4 py-2.5 text-[0.8125rem]">
            Failed to load diagnostics: {error}
          </div>
        )}

        {!data && loading && (
          <div className="text-slate-500 dark:text-slate-400 text-[0.8125rem]">Running checks…</div>
        )}

        {data && (
          <>
            {/* Redis */}
            <Card
              title="Redis (Socket.IO multi-worker)"
              right={<Pill ok={data.redis.checks.some((c) => c.ok)} label={data.redis.checks.some((c) => c.ok) ? 'reachable' : 'unreachable'} />}
            >
              <KV k="USE_REDIS_SOCKETIO">{String(data.redis.use_redis_socketio)}</KV>
              <KV k="host">{data.redis.host}</KV>
              <KV k="port">{data.redis.port}</KV>
              <KV k="password">{data.redis.password}</KV>
              <div className="pt-1 space-y-1.5">
                {data.redis.checks.map((c, i) => (
                  <div key={i} className="flex items-center justify-between border border-slate-100 dark:border-slate-700 rounded px-2.5 py-1.5">
                    <span className="text-[0.75rem]">{c.method}</span>
                    <span className="flex items-center gap-2">
                      {typeof c.ms === 'number' && <span className="text-[0.6875rem] text-slate-400">{c.ms}ms</span>}
                      <Pill ok={c.ok} label={c.ok ? `ping ${c.ping}` : 'fail'} />
                    </span>
                  </div>
                ))}
                {data.redis.checks.filter((c) => !c.ok && c.error).map((c, i) => (
                  <div key={'e' + i} className="text-[0.75rem] text-red-600 dark:text-red-400 break-all">
                    {c.method}: {c.error}
                  </div>
                ))}
              </div>
            </Card>

            {/* Database */}
            <Card
              title="Database engines & pools"
              right={data.database.live_falling_back_to_app ? <Pill ok={false} label="live → app fallback" /> : <Pill ok label="dedicated live engine" />}
            >
              {data.database.live_falling_back_to_app && (
                <div className="text-[0.75rem] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded px-2.5 py-1.5">
                  LIVE_DATABASE_* is not configured — all "live" reads run through the app pool via fallback.
                </div>
              )}
              <EngineRow e={data.database.app} />
              <EngineRow e={data.database.live} />
              <EngineRow e={data.database.replica} />
            </Card>

            {/* Auth */}
            <Card title="Auth / JWT">
              <KV k="allowed algorithms">{data.auth.allowed_jwt_algorithms.join(', ')}</KV>
              <KV k="guard token alg">{data.auth.guard_jwt_algorithm}</KV>
              <KV k="tenant id set">{String(data.auth.tenant_id_set)}</KV>
              <KV k="api audience set">{String(data.auth.api_audience_set)}</KV>
              <p className="text-[0.75rem] text-slate-500 dark:text-slate-400 pt-1">{data.auth.note}</p>
            </Card>

            {/* Dispatch live updates */}
            {data.dispatch_live && (
              <Card
                title="Dispatch Live Updates"
                right={
                  data.dispatch_live.push_enabled === null ? (
                    <Pill ok={false} label="switch unreadable" />
                  ) : (
                    <Pill
                      ok={data.dispatch_live.push_enabled}
                      label={data.dispatch_live.push_enabled ? 'push on' : 'push off'}
                    />
                  )
                }
              >
                <Metric k="pushes sent" hint="since worker start">
                  {data.dispatch_live.pushes.total}
                </Metric>
                <Metric k="last push">{fmtAge(data.dispatch_live.pushes.seconds_since_last)}</Metric>
                {Object.keys(data.dispatch_live.pushes.by_reason).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pl-44">
                    {Object.entries(data.dispatch_live.pushes.by_reason).map(([reason, n]) => (
                      <span
                        key={reason}
                        className="px-2 py-0.5 rounded-full text-[0.6875rem] bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
                      >
                        {reason} · {n}
                      </span>
                    ))}
                  </div>
                )}

                <div className="pt-2 mt-1 border-t border-slate-100 dark:border-slate-700" />

                <Metric
                  k="count cache hit rate"
                  hint={`${data.dispatch_live.bucket_counts.hits} hit / ${data.dispatch_live.bucket_counts.misses} miss`}
                >
                  {data.dispatch_live.bucket_counts.hit_rate_pct === null
                    ? 'no traffic yet'
                    : `${data.dispatch_live.bucket_counts.hit_rate_pct}%`}
                </Metric>
                <Metric k="real scans run" hint={`${data.dispatch_live.bucket_counts.ttl_seconds}s TTL`}>
                  {data.dispatch_live.bucket_counts.scans}
                </Metric>
                <Metric k="scan time (last / p95)">
                  {data.dispatch_live.bucket_counts.last_scan_ms === null
                    ? '—'
                    : `${data.dispatch_live.bucket_counts.last_scan_ms}ms / ${data.dispatch_live.bucket_counts.p95_scan_ms}ms`}
                </Metric>
                <Metric k="cached filter sets" hint={`${data.dispatch_live.bucket_counts.in_flight} computing now`}>
                  {data.dispatch_live.bucket_counts.entries}
                </Metric>

                <p className="text-[0.6875rem] text-slate-400 pt-1">
                  Counters are per-worker and reset on restart — this worker has been up{' '}
                  {Math.round(data.dispatch_live.worker_uptime_seconds / 60)}m. Production runs several
                  workers, so refreshing may report a different one. Watch the replica pool above for
                  load: a rising p95 scan time or checked-out count is the signal to turn push off.
                </p>
                {data.dispatch_live.push_enabled_error && (
                  <p className="text-[0.6875rem] text-red-500">{data.dispatch_live.push_enabled_error}</p>
                )}
              </Card>
            )}

            {/* Logs */}
            <Card title="Recent error counts (container logs)">
              {!data.logs.available ? (
                <p className="text-[0.75rem] text-slate-500 dark:text-slate-400">{data.logs.note}</p>
              ) : (
                <>
                  {data.logs.counts && Object.entries(data.logs.counts).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between border border-slate-100 dark:border-slate-700 rounded px-2.5 py-1.5">
                      <span className="text-[0.75rem]">{k}</span>
                      <Pill ok={v === 0} label={String(v)} />
                    </div>
                  ))}
                  <p className="text-[0.6875rem] text-slate-400 pt-1">
                    files: {data.logs.files_scanned?.join(', ')}
                  </p>
                </>
              )}
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default SystemDiagnostics;
